import {TFunction} from 'i18next';
import {observer} from 'mobx-react-lite';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {
  EDynamicTooltip,
  EIconButton,
  ELinearProgressBar,
  EUndecoratedButton,
} from 'webapp-lib/Components';

import {ProgressBar, SimplePlaceholder} from '#';
import {Close, Sync} from '_/assets/images';
import {DirectUpload} from '_/store/DirectUpload';
import {
  DocumentFileUploadsByState,
  UploadQueueEntry,
} from '_/store/DocumentUpload.store';
import {getRouteUrl} from '_/utilities';

import {
  AllItemsActionWrapper,
  CasusAndFileNameWrapper,
  PendingUploadQueueItem,
  StyledUploadQueue,
  UploadQueueFileName,
  UploadQueueItem,
  UploadQueueItemCasus,
} from './UploadQueue.styles';

const CasusAndFileName = ({
  fileName,
  casusDisplayNameWithClient,
  linkToDocument,
  closePopup,
}: {
  fileName: string;
  casusDisplayNameWithClient: string | undefined;
  linkToDocument?: string;
  closePopup?: () => void;
}) => (
  <CasusAndFileNameWrapper>
    <EDynamicTooltip title={casusDisplayNameWithClient}>
      <UploadQueueItemCasus>{casusDisplayNameWithClient}</UploadQueueItemCasus>
    </EDynamicTooltip>
    <EDynamicTooltip title={fileName}>
      <UploadQueueFileName>
        {linkToDocument ? (
          <Link to={linkToDocument} onClick={closePopup}>
            {fileName}
          </Link>
        ) : (
          fileName
        )}
      </UploadQueueFileName>
    </EDynamicTooltip>
  </CasusAndFileNameWrapper>
);

const UPendingItemList = ({
  items,
  abortAll,
}: {
  items: UploadQueueEntry[];
  abortAll: () => void;
}) => {
  const {t} = useTranslation('documents');

  if (items.length === 0) {
    return <SimplePlaceholder customText={t('uploadQueue.noPendingItems')} />;
  }

  return (
    <>
      <AllItemsActionWrapper>
        <EUndecoratedButton onClick={abortAll}>
          {t('uploadQueue.abortAllPendingItems')}
        </EUndecoratedButton>
      </AllItemsActionWrapper>
      <StyledUploadQueue>
        {items.map((upload) => (
          <PendingUploadQueueItem key={upload.id}>
            <div>
              <CasusAndFileName
                casusDisplayNameWithClient={upload.casusDisplayNameWithClient}
                fileName={getUploadName(upload, t)}
              />
              {isAbortable(upload) ? (
                <EIconButton testId='abortUpload' onClick={upload.abort}>
                  <Close />
                </EIconButton>
              ) : null}
            </div>
            {upload.progress === undefined ? (
              <ELinearProgressBar />
            ) : (
              <ProgressBar showActions={false} value={upload.progress} />
            )}
          </PendingUploadQueueItem>
        ))}
      </StyledUploadQueue>
    </>
  );
};
const PendingItemList = observer(UPendingItemList);

const USuccessfulItemList = ({
  items,
  closePopup,
  clearAll,
}: {
  items: UploadQueueEntry[];
  closePopup: () => void;
  clearAll: () => void;
}) => {
  const {t} = useTranslation('documents');

  if (items.length === 0) {
    return (
      <SimplePlaceholder customText={t('uploadQueue.noSuccessfulItems')} />
    );
  }

  return (
    <>
      <AllItemsActionWrapper>
        <EUndecoratedButton onClick={clearAll}>
          {t('uploadQueue.clearAll')}
        </EUndecoratedButton>
      </AllItemsActionWrapper>
      <StyledUploadQueue>
        {items.map((upload) => (
          <UploadQueueItem key={upload.id}>
            <CasusAndFileName
              casusDisplayNameWithClient={upload.casusDisplayNameWithClient}
              closePopup={closePopup}
              fileName={getUploadName(upload, t)}
              linkToDocument={getRouteUrl({
                module: 'casus-documents',
                casusId: upload.casusId,
                folderId: upload.folderId,
                documentId:
                  upload instanceof DirectUpload
                    ? undefined
                    : upload.createdDocumentId,
                clearFilters: true,
              })}
            />
          </UploadQueueItem>
        ))}
      </StyledUploadQueue>
    </>
  );
};
const SuccessfulItemList = observer(USuccessfulItemList);

const UFailedItemList = ({
  items,
  retry,
  clearAll,
}: {
  items: UploadQueueEntry[];
  retry: (upload: UploadQueueEntry) => void;
  clearAll: () => void;
}) => {
  const {t} = useTranslation('documents');

  if (items.length === 0) {
    return <SimplePlaceholder customText={t('uploadQueue.noFailedItems')} />;
  }

  return (
    <>
      <AllItemsActionWrapper>
        <EUndecoratedButton onClick={clearAll}>
          {t('uploadQueue.clearAll')}
        </EUndecoratedButton>
      </AllItemsActionWrapper>
      <StyledUploadQueue>
        {items.map((upload) => (
          <UploadQueueItem key={upload.id}>
            <CasusAndFileName
              casusDisplayNameWithClient={upload.casusDisplayNameWithClient}
              fileName={getUploadName(upload, t)}
            />
            <EIconButton onClick={() => retry(upload)}>
              <Sync />
            </EIconButton>
          </UploadQueueItem>
        ))}
      </StyledUploadQueue>
    </>
  );
};
const FailedItemList = observer(UFailedItemList);

const UUploadQueueLists = ({
  activeTabItem,
  items,
  closePopup,
  clearAllSuccessfulFiles,
  clearAllFailedFiles,
  abortAllPendingFiles,
  retryUpload,
}: {
  activeTabItem: 'PENDING' | 'SUCCESSFUL' | 'FAILED';
  items: DocumentFileUploadsByState;
  closePopup: () => void;
  clearAllSuccessfulFiles: () => void;
  clearAllFailedFiles: () => void;
  abortAllPendingFiles: () => void;
  retryUpload: (upload: UploadQueueEntry) => void;
}) => {
  switch (activeTabItem) {
    case 'PENDING':
      return (
        <PendingItemList
          abortAll={abortAllPendingFiles}
          items={items.pending}
        />
      );
    case 'SUCCESSFUL':
      return (
        <SuccessfulItemList
          clearAll={clearAllSuccessfulFiles}
          closePopup={closePopup}
          items={items.successful}
        />
      );
    case 'FAILED':
      return (
        <FailedItemList
          clearAll={clearAllFailedFiles}
          items={items.failed}
          retry={retryUpload}
        />
      );
  }
};
export const UploadQueueLists = observer(UUploadQueueLists);

function getUploadName(
  upload: UploadQueueEntry,
  t: TFunction<'documents'>
): string {
  if (!(upload instanceof DirectUpload)) {
    return upload.name;
  }
  return upload.documentCount === null
    ? ''
    : t('uploadQueue.fileCount', {count: upload.documentCount});
}

function isAbortable(upload: UploadQueueEntry): boolean {
  return !(upload instanceof DirectUpload) || upload.abortable;
}
